import React, { useEffect, useState } from 'react';
import { useData } from '../contexts/DataContext';
import StatCard from '../components/ui/StatCard';
import PaymentRow from '../components/ui/PaymentRow';
import useMediaQuery from '../hooks/useMediaQuery';
import { getPayments } from '../services/paymentService';

function formatCurrency(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(n);
}

export default function PaymentsPage() {
  const { getCachedPayments, getStale } = useData();
  const stale = getStale('payments');
  const [payments, setPayments] = useState(stale);
  const [loading, setLoading] = useState(!stale);
  const [error, setError] = useState(null);
  const [paidCursor, setPaidCursor] = useState(stale?.pagination?.paid?.nextCursor || null);
  const [hasMorePaid, setHasMorePaid] = useState(stale?.pagination?.paid?.hasMore || false);
  const [loadingMorePaid, setLoadingMorePaid] = useState(false);
  const [loadingMoreResults, setLoadingMoreResults] = useState(false);
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setError(null);
        const data = await getCachedPayments();
        if (mounted) {
          setPayments(data);
          setPaidCursor(data?.pagination?.paid?.nextCursor || null);
          setHasMorePaid(data?.pagination?.paid?.hasMore || false);
        }
      } catch {
        if (mounted) setError('Failed to load payments.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [getCachedPayments]);

  if (loading && !payments) return <div className="loading-state">Loading...</div>;
  if (error && !payments) {
    return (
      <div className="error-state">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const stats = payments?.stats || {};
  const upcoming = payments?.upcoming || [];
  const pending = payments?.pending || [];
  const paid = payments?.paid || [];
  const takeoverResults = payments?.takeoverResults?.items || [];

  const loadMorePaid = async () => {
    if (!paidCursor || loadingMorePaid) return;
    setLoadingMorePaid(true);
    try {
      const res = await getPayments({ cursor: paidCursor });
      const next = res.data || {};
      setPayments(prev => ({
        ...prev,
        paid: [...(prev?.paid || []), ...(next.paid || [])],
        pagination: next.pagination || prev?.pagination
      }));
      setPaidCursor(next.pagination?.paid?.nextCursor || null);
      setHasMorePaid(next.pagination?.paid?.hasMore || false);
    } catch {
      setError('Failed to load more payments.');
    } finally {
      setLoadingMorePaid(false);
    }
  };

  const loadMoreResults = async () => {
    const cursor = payments?.takeoverResults?.nextCursor;
    if (!cursor || loadingMoreResults) return;
    setLoadingMoreResults(true);
    setError(null);
    try {
      const res = await getPayments({ takeoverCursor: cursor });
      const next = res.data?.takeoverResults;
      setPayments(prev => ({ ...prev, takeoverResults: {
        ...next, items: [...(prev.takeoverResults?.items || []), ...(next?.items || [])]
      } }));
    } catch {
      setError('Failed to load more campaign results. Try again.');
    } finally { setLoadingMoreResults(false); }
  };

  return (
    <>
      <div className="sec-eyebrow">Payment History</div>
      <div className="sec-title">Payments</div>
      <div className="sec-sub">All your earnings from TAKEOVER campaigns.</div>
      {error && <p role="alert">{error} Previously loaded payments are still shown.</p>}

      <div className="stats" style={{ gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)' }}>
        <StatCard
          label="Total Earned"
          value={formatCurrency(stats.totalEarned)}
          sub="Lifetime earnings"
          primary
        />
        <StatCard
          label="Pending"
          value={formatCurrency(stats.pendingAmount)}
          sub={pending.length > 0 ? `${pending.length} payments processing` : undefined}
        />
        <StatCard
          label="Upcoming"
          value={formatCurrency(stats.upcomingAmount)}
          sub="From active campaigns"
        />
      </div>

      {takeoverResults.length > 0 && (
        <section aria-label="Timeline Takeover results">
          <div className="sec-head">Timeline Takeover Results</div>
          <div className="sec-sub">Campaign results are separate from settlement. A rank or finalized score does not confirm a payout; confirmed payments appear in Completed below.</div>
          {takeoverResults.map(result => (
            <div className="brief-section" key={result.campaignId} style={{ padding: 24 }}>
              <a className="brief-ref" href={`/takeover/${result.campaignId}`}>{result.campaignName}</a>
              <p>{result.status === 'finalized' ? 'Results finalized' : result.status === 'reviewing' ? 'Final review in progress' : 'Provisional result'}</p>
              <p>{result.rank > 0 ? `Rank #${result.rank}` : 'No eligible rank yet'}{result.score != null ? ` · Score ${Number(result.score).toFixed(1)}` : ''}</p>
              {result.status === 'finalized' && result.entitlement?.status === 'earned' && <p>
                Award earned: {formatCurrency(result.entitlement.amountUsdc)} USDC. Check Completed for recorded payment confirmation.
              </p>}
              {result.status === 'finalized' && result.entitlement?.status === 'not_eligible' && <p>No award under this campaign's published payout schedule.</p>}
              {result.status === 'finalized' && (!result.entitlement || result.entitlement.status === 'not_configured') && <p>Award details require confirmation by the campaign team.</p>}
              {result.reason && <p>{result.reason}</p>}
            </div>
          ))}
          {payments.takeoverResults.hasMore && <button onClick={loadMoreResults} disabled={loadingMoreResults}>
            {loadingMoreResults ? 'Loading results...' : 'Load More Results'}
          </button>}
        </section>
      )}

      {upcoming.length > 0 && (
        <>
          <div className="sec-head">Upcoming</div>
          <div className="pay-scroll">
            <div className="pay-card">
              <div className="pay-item header">
                <span>Campaign</span>
                <span>Amount</span>
                <span>Status</span>
                <span>Expected</span>
                <span></span>
              </div>
              {upcoming.map((p, i) => (
                <PaymentRow key={i} payment={{ ...p, status: 'upcoming', dateLabel: 'After delivery' }} />
              ))}
            </div>
          </div>
        </>
      )}

      {pending.length > 0 && (
        <>
          <div className="sec-head">Pending</div>
          <div className="pay-scroll">
            <div className="pay-card">
              <div className="pay-item header">
                <span>Campaign</span>
                <span>Amount</span>
                <span>Status</span>
                <span>Date</span>
                <span></span>
              </div>
              {pending.map((p, i) => (
                <PaymentRow key={i} payment={{ ...p, status: 'pending' }} />
              ))}
            </div>
          </div>
        </>
      )}

      {paid.length > 0 && (
        <>
          <div className="sec-head">Completed</div>
          <div className="pay-scroll">
            <div className="pay-card">
              <div className="pay-item header">
                <span>Campaign</span>
                <span>Amount</span>
                <span>Status</span>
                <span>Date</span>
                <span>Transaction</span>
              </div>
              {paid.map((p, i) => (
                <PaymentRow key={p.id || i} payment={{ ...p, status: 'paid' }} />
              ))}
            </div>
          </div>
          {hasMorePaid && (
            <div className="lb-more">
              <button onClick={loadMorePaid} disabled={loadingMorePaid}>
                {loadingMorePaid ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {upcoming.length === 0 && pending.length === 0 && paid.length === 0 && (
        <div style={{ color: '#555', fontFamily: "'Space Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', padding: '40px 0' }}>
          No payment history yet.
        </div>
      )}
    </>
  );
}
